import { Assert } from "@seriously-rad/seriouslyrad-framework-core/Assert";
import { Nullable, PartialNullable } from "@seriously-rad/seriouslyrad-framework-core/Extensions";
import { ObjectHelper } from "@seriously-rad/seriouslyrad-framework-core/Helpers/ObjectHelper";
import { IJNativeEvent } from "@seriously-rad/seriouslyrad-framework-core/JNative";
import { PropertyChange } from "@seriously-rad/seriouslyrad-framework-core/PropertyChange";
import { Property, PropertyDecimal, PropertyInt32, PropertyObject } from "@seriously-rad/seriouslyrad-framework-core/RadObject";
import { ClientAwareCategoryAttribute } from "@seriously-rad/seriouslyrad-framework-core/Server/DotNet/SeriouslyRAD.Framework/ClientAwareCategoryAttribute";
import { ClientAwareDisplayNameAttribute } from "@seriously-rad/seriouslyrad-framework-core/Server/DotNet/SeriouslyRAD.Framework/ClientAwareDisplayNameAttribute";
import { ITypeInfoMetadata } from "@seriously-rad/seriouslyrad-framework-core/Server/DotNet/SeriouslyRAD.Framework/Reflection/ITypeInfoMetadata";
import { BuiltInTypes } from "@seriously-rad/seriouslyrad-framework-core/Server/DotNet/System/BuiltInTypes";
import { ClassPropertyDialog } from "@seriously-rad/seriouslyrad-framework-core/Ui/Controls/ClassPropertyDialog";
import { IDrawingElement } from "./ReportElements/DrawingElement";
import { IReportTemplate } from "./ReportTemplate";
import { IResizableControl, ResizableControl, ResizableControlPropertyValues } from "./ResizableControl";
import { NAMESPACE } from "./_Namespace";

import "./ReportElement.scss";

export type ReportElementPropertyValues =
    ResizableControlPropertyValues
    & {
        gridSpacing: number;
        templateProperties: any;
        templateTypeInfoMetadata: ITypeInfoMetadata;
        zoom: number;
    };

export type ReportElementProperties = {
    [name in keyof ReportElementPropertyValues]: Property<ReportElementPropertyValues[name]>
};

export interface IReportElement extends IResizableControl, ReportElementProperties
{
    alignToGrid(): void;
}

export abstract class ReportElement extends ResizableControl implements IReportElement
{
    // #region Static

    public static override classNamespace = NAMESPACE + "ReportElement";
    public static override className = "ReportElement";

    // #region Overrides

    public static override createPropertyDefaults(): PartialNullable<ReportElementPropertyValues>
    {
        return {
            ...super.createPropertyDefaults(),
            ...{
                tabStop: true,
                zoom: 1,
            }
        };
    }

    // #endregion

    // #endregion

    // #region Instance

    // #region Global Variables

    private _classPropertyDialog?: ClassPropertyDialog;
    private _loadingTemplateProperties?: boolean;
    private _savingTemplateProperties?: boolean;

    // #endregion

    // #region Properties

    public gridSpacing!: PropertyInt32;
    public templateProperties!: PropertyObject;
    public templateTypeInfoMetadata!: Property<ITypeInfoMetadata>;
    public zoom!: PropertyDecimal;

    // #endregion

    // #region Constructor

    constructor(propertyValues?: PartialNullable<ReportElementPropertyValues>)
    {
        super(propertyValues);

        this.addCssClass("rad-control-report-element");

        // Trigger Property Changes
        this.gridSpacing.triggerChange();
        this.zoom.triggerChange();

        this.templateProperties.triggerChange();
        this.templateTypeInfoMetadata.triggerChange();
    }

    // #endregion

    // #region Public Methods

    public alignToGrid(): void
    {
        this.top.set(this._valueToGrid(this.top.get() ?? 0));
        this.left.set(this._valueToGrid(this.left.get() ?? 0));

        this.width.set(this._valueToGrid(this.width.get() ?? 0, 1));
        this.height.set(this._valueToGrid(this.height.get() ?? 0, 1));
    }

    public templatePropertiesLoad(): void
    {
        if (this._savingTemplateProperties)
            return;

        if (this._loadingTemplateProperties)
            return;

        const
            templateProperties = this.templateProperties.get();

        if (templateProperties)
        {
            this._loadingTemplateProperties = true;
            this._templatePropertiesLoad(templateProperties);
            this._loadingTemplateProperties = false;
        }
    }
    public templatePropertiesSave(): void
    {
        if (this._loadingTemplateProperties)
            return;

        if (this._savingTemplateProperties)
            return;

        const
            templateProperties = this.templateProperties.get();

        if (templateProperties)
        {
            this._savingTemplateProperties = true;
            this._templatePropertiesSave(templateProperties);
            this._savingTemplateProperties = false;
        }
    }

    // #endregion

    // #region Protected Methods

    public _getReportTemplate(): Nullable<IReportTemplate>
    {
        return this.$$element.get()?.closest(".rad-control-report-template").radControl() as Nullable<IReportTemplate>;
    }
    public _getTemplatePropertyValue(propertyPath: string): any
    {
        const
            templateProperties = this.templateProperties.get();

        return ObjectHelper.propertyValueByPathGet(templateProperties, propertyPath) ?? null;
    }
    protected _gridSpacingChanged(change: PropertyChange<number>)
    {
        this._updateResizeBandGridSpacing();
    }
    protected _reset()
    {
        // TODO - Remove?
    }
    protected _resizeReportSections()
    {
        const
            template = this._getReportTemplate();

        if (template)
            template._resizeReportSections();
    }
    public _setTemplatePropertyValue(propertyPath: string, value?: any)
    {
        const
            templatePropertiesClone = ObjectHelper.cloneObject(this.templateProperties.get() ?? {});

        ObjectHelper.propertyValueByPathSet(templatePropertiesClone, propertyPath, value);

        this.templateProperties.set(templatePropertiesClone);
    }
    protected _templatePropertiesChanged(change: PropertyChange<any>)
    {
        const
            templateProperties = change.currentValue,
            bottom = templateProperties?.bottom ?? 0,
            left = templateProperties?.left ?? 0,
            right = templateProperties?.right ?? 0,
            top = templateProperties?.top ?? 0,
            zoom = this.zoom.get();

        Assert.isNotNullOrUndefined(zoom, "zoom");

        const
            isDrawingElement = ObjectHelper.classInheritsClassName<IDrawingElement>(this, "@seriously-rad/seriouslyrad-framework-module-reports/Ui/Controls/PrintReportDesigner/Controls/ReportElements/DrawingElement.DrawingElement");

        if (!isDrawingElement)
            this.height.set((bottom - top) * zoom);

        this.left.set(left * zoom);
        this.top.set(top * zoom);
        this.width.set((right - left) * zoom);
    }
    protected _templatePropertiesLoad(templateProperties: any): void
    {
        const
            bottom = templateProperties?.bottom ?? 0,
            left = templateProperties?.left ?? 0,
            right = templateProperties?.right ?? 0,
            top = templateProperties?.top ?? 0,
            zoom = this.zoom.get();

        Assert.isNotNullOrUndefined(zoom, "zoom");

        const
            isDrawingElement = ObjectHelper.classInheritsClassName<IDrawingElement>(this, "@seriously-rad/seriouslyrad-framework-module-reports/Ui/Controls/PrintReportDesigner/Controls/ReportElements/DrawingElement.DrawingElement");

        if (!isDrawingElement)
            this.height.set((bottom - top) * zoom);

        this.left.set(left * zoom);
        this.top.set(top * zoom);
        this.width.set((right - left) * zoom);
    }
    protected _templatePropertiesSave(templateProperties: any): void
    {
        const
            height = this.height.get() ?? 0,
            left = this.left.get() ?? 0,
            top = this.top.get() ?? 0,
            width = this.width.get() ?? 0,
            zoom = this.zoom.get() ?? 1,
            isDrawingElement = ObjectHelper.classInheritsClassName<IDrawingElement>(this, "@seriously-rad/seriouslyrad-framework-module-reports/Ui/Controls/PrintReportDesigner/Controls/ReportElements/DrawingElement.DrawingElement");

        if (!isDrawingElement)
            templateProperties.bottom = (top + height) / zoom;

        templateProperties.left = left / zoom;
        templateProperties.right = (left + width) / zoom;
        templateProperties.top = top / zoom;
    }
    protected _templateTypeInfoMetadataChanged(change: PropertyChange<ITypeInfoMetadata>)
    {
        this.templateProperties.dotNetType = change.currentValue?.underlyingTypeId as BuiltInTypes ?? "System.Object, System.Private.CoreLib";
    }
    protected _updateResizeBandGridSpacing()
    {
        if (!this.resizeBand)
            return;

        this.resizeBand.gridSpacing.set((this.gridSpacing.get() ?? 1) * (this.zoom.get() ?? 1));
    }
    protected _valueToGrid(value: number, minimumSizeInGridUnits?: number): number
    {
        const
            gridSpacing = this.gridSpacing.get() ?? 1,
            zoom = this.zoom.get() ?? 1,
            zoomedGridSpacing = gridSpacing * zoom;

        let
            snapped = Math.round(value / zoomedGridSpacing) * zoomedGridSpacing;

        if (minimumSizeInGridUnits && snapped < minimumSizeInGridUnits * zoomedGridSpacing)
            snapped = minimumSizeInGridUnits * zoomedGridSpacing;

        return snapped;
    }
    protected _zoomChanged(change: PropertyChange<number>)
    {
        this.templatePropertiesLoad();
        this._updateResizeBandGridSpacing();
    }

    // #endregion

    // #region Overrides

    protected override __initializeProperties(propertyValues: ReportElementPropertyValues)
    {
        super.__initializeProperties(propertyValues);

        this.gridSpacing = new PropertyInt32(this, propertyValues.gridSpacing, this._gridSpacingChanged);

        this.templateProperties =
            new PropertyObject(
                this,
                propertyValues.templateProperties,
                this._templatePropertiesChanged,
                undefined,
                [
                    new ClientAwareCategoryAttribute({ category: "Service Properties" }),
                    new ClientAwareDisplayNameAttribute({ displayName: "Properties" }),
                ],
            );

        this.templateTypeInfoMetadata = new Property<ITypeInfoMetadata>(this, "System.Object, System.Private.CoreLib", propertyValues.templateTypeInfoMetadata, this._templateTypeInfoMetadataChanged);
        this.zoom = new PropertyDecimal(this, propertyValues.zoom, this._zoomChanged);
    }

    protected override _bindEvents()
    {
        super._bindEvents();

        this.on("doubleClick", { self: this }, this.onDoubleClick);
    }
    protected override _heightChanged(change: PropertyChange<number>)
    {
        super._heightChanged(change);
        this.templatePropertiesSave();
    }
    protected override _leftChanged(change: PropertyChange<number>)
    {
        super._leftChanged(change);
        this.templatePropertiesSave();
    }
    protected override _topChanged(change: PropertyChange<number>)
    {
        super._topChanged(change);
        this.templatePropertiesSave();
    }
    protected override _widthChanged(change: PropertyChange<number>)
    {
        super._widthChanged(change);
        this.templatePropertiesSave();
    }
    protected override onResizeBandMoveEnd(evt: IJNativeEvent)
    {
        const
            self = evt.data.self as this;

        self._resizeReportSections();
    }
    protected override onResizeBandResizeEnd(evt: IJNativeEvent)
    {
        const
            self = evt.data.self as this;

        self._resizeReportSections();
    }

    // #endregion

    // #region Event Handlers

    protected async onDoubleClick(evt: IJNativeEvent)
    {
        const
            self = evt.data.self as this;

        if (!self._classPropertyDialog)
            self._classPropertyDialog = new ClassPropertyDialog();

        await self._classPropertyDialog.openAsync(self);
    }

    // #endregion

    // #endregion
}